import React from 'react';
import {
  Activity,
  Calendar,
  DollarSign,
  StickyNote,
  Users,
  Settings,
  ChevronRight,
  Pin,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFetchClientNotes } from '@/hooks/useClientNotes';
import { useFetchClientActivityFeed } from '@/hooks/useClients';
import { formatDistanceToNow, isAfter, subDays, format } from 'date-fns';

type ProfileTab = 'summary' | 'activity' | 'bookings' | 'financial' | 'notes' | 'config' | 'contacts';

interface MobileSummaryProps {
  clientId: string;
  onNavigateToTab: (tab: ProfileTab) => void;
}

const SECTION_LINKS: { tab: ProfileTab; label: string; icon: React.ReactNode }[] = [
  { tab: 'activity', label: 'Activity Feed', icon: <Activity className="h-4 w-4" /> },
  { tab: 'bookings', label: 'Bookings', icon: <Calendar className="h-4 w-4" /> },
  { tab: 'financial', label: 'Financial', icon: <DollarSign className="h-4 w-4" /> },
  { tab: 'notes', label: 'Notes', icon: <StickyNote className="h-4 w-4" /> },
  { tab: 'contacts', label: 'Contacts', icon: <Users className="h-4 w-4" /> },
  { tab: 'config', label: 'Configuration', icon: <Settings className="h-4 w-4" /> },
];

export const MobileSummary: React.FC<MobileSummaryProps> = ({ clientId, onNavigateToTab }) => {
  const { data: notes = [], isLoading: notesLoading } = useFetchClientNotes(clientId);
  const { data: activity = [], isLoading: activityLoading, error: activityError } = useFetchClientActivityFeed(clientId, { limit: 8 });

  const pinnedNotes = notes.filter((n) => n.pinned);

  return (
    <div className="space-y-4">
      {/* Pinned notes */}
      {pinnedNotes.length > 0 && (
        <div className="space-y-2">
          <h2 className="font-heading text-xs uppercase tracking-wider text-primary flex items-center gap-1.5">
            <Pin className="h-3.5 w-3.5" />
            Pinned Notes
          </h2>
          {pinnedNotes.map((note) => (
            <div
              key={note.id}
              className="bg-surface-1 border border-primary/20 rounded px-3 py-2"
            >
              <p className="text-sm text-foreground whitespace-pre-wrap break-words line-clamp-3">
                {note.content}
              </p>
              <p className="text-[10px] text-muted-foreground mt-1">
                {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Recent activity */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xs uppercase tracking-wider text-muted-foreground">
            Recent Activity
          </h2>
          <button
            onClick={() => onNavigateToTab('activity')}
            className="text-xs text-primary hover:brightness-110 font-label uppercase tracking-wider"
          >
            View All
          </button>
        </div>

        {activityLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          </div>
        ) : activityError ? (
          <p className="text-sm text-destructive text-center py-4">Failed to load activity.</p>
        ) : activity.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">No recent activity.</p>
        ) : (
          <div className="space-y-1">
            {activity.map((item, i) => {
              const date = new Date(item.event_date);
              const sevenDaysAgo = subDays(new Date(), 7);
              const dateStr = isAfter(date, sevenDaysAgo)
                ? formatDistanceToNow(date, { addSuffix: true })
                : format(date, 'MMM d');

              return (
                <div
                  key={`${item.event_type}-${item.event_date}-${i}`}
                  className="flex items-center gap-2 py-1.5 px-2 bg-surface-1 border border-border rounded text-sm"
                >
                  <span className="flex-1 truncate text-foreground">{item.summary}</span>
                  <span className="text-[10px] text-muted-foreground font-mono flex-shrink-0">
                    {dateStr}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Section links */}
      <div className="space-y-1 pt-2">
        <h3 className="font-heading text-xs uppercase tracking-wider text-muted-foreground mb-2">
          Sections
        </h3>
        {SECTION_LINKS.map(({ tab, label, icon }) => (
          <button
            key={tab}
            onClick={() => onNavigateToTab(tab)}
            className="flex items-center gap-3 w-full px-3 py-2.5 bg-surface-1 border border-border rounded hover:bg-surface-2 transition-colors text-left"
          >
            <span className="text-muted-foreground">{icon}</span>
            <span className="flex-1 text-sm text-foreground">{label}</span>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </button>
        ))}
      </div>
    </div>
  );
};
